import { dateKey, parseAnchor } from './state';

/* ── Daily weigh-ins ───────────────────────────────────────────────────────
 * bodyMetrics is a date-sorted [{ date, weight }], one entry per day. */

export function getWeightOn(bodyMetrics, dayKey) {
  const hit = (bodyMetrics || []).find(m => m.date === dayKey);
  return hit && hit.weight != null ? hit.weight : null;
}

/* Upsert a day's weight. A null/0/blank weight removes the entry, so clearing
 * the field undoes a mistyped weigh-in rather than recording a 0 kg day. */
export function upsertWeight(bodyMetrics, dayKey, weight) {
  const rest = (bodyMetrics || []).filter(m => m.date !== dayKey);
  const w = Number(weight);
  if (!w || w <= 0) return rest.sort((a, b) => a.date.localeCompare(b.date));
  return [...rest, { date: dayKey, weight: w }].sort((a, b) => a.date.localeCompare(b.date));
}

/* Scale weight is noisy — water, salt, food still in transit, time of day —
 * so a single day says almost nothing and reacting to it is how people talk
 * themselves out of a working plan. The 7-day average against the 7 before it
 * is the number that actually shows direction, so that's what the UI leads
 * with. Needs 2+ readings in a window before it claims a trend. */
export function weightTrend(bodyMetrics, dayKey) {
  const end = parseAnchor(dayKey);
  if (!end) return null;
  const list = (bodyMetrics || []).filter(m => m.date <= dayKey && m.weight > 0);
  if (!list.length) return null;

  const windowMean = (fromDaysAgo, toDaysAgo) => {
    const lo = new Date(end); lo.setDate(lo.getDate() - fromDaysAgo);
    const hi = new Date(end); hi.setDate(hi.getDate() - toDaysAgo);
    const loKey = dateKey(lo);
    const hiKey = dateKey(hi);
    const vals = list.filter(m => m.date >= loKey && m.date <= hiKey).map(m => m.weight);
    return vals.length ? { mean: vals.reduce((a, b) => a + b, 0) / vals.length, n: vals.length } : null;
  };

  const recent = windowMean(6, 0);
  const prior = windowMean(13, 7);
  const latest = list[list.length - 1];

  return {
    latest: latest.weight,
    latestDate: latest.date,
    avg: recent ? recent.mean : null,
    avgDays: recent ? recent.n : 0,
    // kg/week: only meaningful once both windows have real readings.
    perWeek: recent && prior && recent.n >= 2 && prior.n >= 2 ? recent.mean - prior.mean : null,
  };
}

/* Is the scale going the way the goal wants? Returns 'good' | 'bad' | null —
 * null when the goal has no obvious direction (recomp, strength) or the move
 * is inside the noise floor. */
export function weightVerdict(perWeek, goal) {
  if (perWeek == null || Math.abs(perWeek) < 0.15) return null;
  if (goal === 'lose fat') return perWeek < 0 ? 'good' : 'bad';
  if (goal === 'build muscle') return perWeek > 0 ? 'good' : 'bad';
  if (goal === 'maintenance') return Math.abs(perWeek) < 0.4 ? 'good' : 'bad';
  return null;
}

/* Daily weight + its 7-day average, for charting. One point per day that has
 * a reading; the average is over the trailing 7 days of readings. */
export function buildWeightSeries(bodyMetrics) {
  const list = [...(bodyMetrics || [])].filter(m => m.weight > 0).sort((a, b) => a.date.localeCompare(b.date));
  return list.map(entry => {
    const d = parseAnchor(entry.date);
    const lo = new Date(d); lo.setDate(lo.getDate() - 6);
    const loKey = dateKey(lo);
    const vals = list.filter(m => m.date >= loKey && m.date <= entry.date).map(m => m.weight);
    return {
      date: entry.date,
      label: d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : entry.date,
      weight: Math.round(entry.weight * 10) / 10,
      avg: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10,
    };
  });
}

/* Consecutive days up to and including `dayKey` on which a supplement was
 * logged. Today not being ticked yet doesn't break the streak — it just isn't
 * counted — so the number only drops once a day has actually been missed. */
export function supplementStreak(log, id, dayKey) {
  const start = parseAnchor(dayKey);
  if (!start) return 0;
  const cursor = new Date(start);
  const takenOn = (d) => ((log || {})[dateKey(d)] || []).includes(id);
  if (!takenOn(cursor)) cursor.setDate(cursor.getDate() - 1);
  let streak = 0;
  while (takenOn(cursor)) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}
